// ------------------ Google Drive API key ------------------

const API_KEY = 'AIzaSyAVWt-gA8JD4VQBmw2JOHjlqUlUcpxvQqg';
const API_URL = 'https://www.googleapis.com/drive/v3/files';

// ----------------------------------------------------------

export const TYPE_IMG = 'image';
export const TYPE_GIF = 'gif';
export const TYPE_VIDEO = 'video';
export const HIDDEN = 'hidden';
export const TYPES = [TYPE_IMG, TYPE_GIF, TYPE_VIDEO];
export const TYPE_LABELS = { [TYPE_IMG]: 'Photos', [TYPE_GIF]: 'GIFs', [TYPE_VIDEO]: 'Videos' };

const FOLDER_MIME = 'application/vnd.google-apps.folder';
const GIF_MAX_AVG_BYTES = 5 * 1024 * 1024; // mp4s smaller than this on average are treated as GIFs
const LEGACY_IMAGE_MIMES = ['image/jpeg', 'image/png'];
const EXTENSIONS = { 'image/jpeg': 'jpg', 'image/svg+xml': 'svg', 'video/quicktime': 'mov' };

export const isFolder = file => file.mimeType === FOLDER_MIME;
// Items that must play in the Drive player instead of streaming through /imageproxy?id=
export const playsInDrivePlayer = item =>
  item.type === TYPE_VIDEO ||
  (/^video\//.test(item.mimeType || '') && Number(item.size || 0) >= GIF_MAX_AVG_BYTES);
export const isMedia = file => /^(image|video)\//.test(file.mimeType || '');
const isVideo = file => /^video\//.test(file.mimeType || '');

export function guessFolderType(files) {
  const media = files.filter(isMedia);
  if (!media.length) return HIDDEN;

  const counts = {};
  media.forEach(file => (counts[file.mimeType] = (counts[file.mimeType] || 0) + 1));

  const gifs = counts['image/gif'] || 0;
  const maxOther = Math.max(0, ...Object.keys(counts).filter(mime => mime !== 'image/gif').map(mime => counts[mime]));
  if (gifs > maxOther) return TYPE_GIF;

  const videos = media.filter(isVideo);
  if (videos.length > media.length - videos.length) {
    const averageSize = videos.reduce((sum, file) => sum + Number(file.size || 0), 0) / videos.length;
    return averageSize < GIF_MAX_AVG_BYTES ? TYPE_GIF : TYPE_VIDEO;
  }

  return TYPE_IMG;
}

export function summarizeFiles(files) {
  const counts = {};
  files.filter(isMedia).forEach(file => {
    const ext = EXTENSIONS[file.mimeType] || file.mimeType.split('/')[1];
    counts[ext] = (counts[ext] || 0) + 1;
  });

  const parts = Object.keys(counts)
    .sort((a, b) => counts[b] - counts[a])
    .map(ext => `${counts[ext]} ${ext}`);

  return parts.length ? parts.join(', ') : 'No media files';
}

export async function listFolder(folderId, fields = 'id,name,mimeType,size') {
  let files = [];
  let pageToken = '';

  do {
    const params = new URLSearchParams({
      key: API_KEY,
      pageSize: '1000',
      q: `trashed=false and '${folderId}' in parents`,
      fields: `nextPageToken,files(${fields})`,
    });
    if (pageToken) params.set('pageToken', pageToken);

    const json = await fetch(`${API_URL}?${params.toString()}`).then(response => response.json());
    if (!json.files) throw new Error(json.error ? json.error.message : 'Cannot read the Google Drive folder.');

    files = files.concat(json.files);
    pageToken = json.nextPageToken;
  } while (pageToken);

  return files;
}

export function loadSources(sources, list = listFolder) {
  return Promise.all(
    sources.map(source =>
      list(source.id)
        .then(files => ({ type: source.type, files }))
        .catch(error => {
          console.log(`Skipping source "${source.name}":`, error.message);
          return { type: source.type, files: [] };
        })
    )
  );
}

function legacyType(file) {
  if (file.mimeType === 'image/gif') return TYPE_GIF;
  if (LEGACY_IMAGE_MIMES.includes(file.mimeType)) return TYPE_IMG;
  return null;
}

export function bucketFiles(sourceResults, excludeNames) {
  const buckets = { [TYPE_IMG]: [], [TYPE_GIF]: [], [TYPE_VIDEO]: [] };

  sourceResults.forEach(({ type, files }) => {
    files.forEach(file => {
      if (!isMedia(file) || excludeNames.includes(file.name)) return;
      const bucket = type || legacyType(file);
      if (bucket && buckets[bucket]) buckets[bucket].push({ ...file, type: bucket });
    });
  });

  return buckets;
}
